import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Alert, Switch,
  FormControlLabel, Divider,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Product {
  id: string;
  name: string;
  sku?: string;
  category?: string;
  brand?: string;
  description?: string;
  unit_of_measure?: string;
  cost_price?: number;
  selling_price?: number;
  tax_percentage?: number;
  is_taxable?: number;
  current_stock: number;
  min_stock_level?: number;
  max_discount_percentage?: number;
}

const UNIT_OPTIONS = ['unit', 'each', 'ml', 'L', 'mg', 'g', 'kg', 'tablet', 'capsule', 'vial', 'ampule', 'box', 'bag', 'tube', 'bottle', 'sachet'];

const emptyProduct = {
  name: '',
  sku: '',
  category: '',
  brand: '',
  description: '',
  unit_of_measure: 'unit',
  cost_price: '',
  selling_price: '',
  tax_percentage: '0',
  is_taxable: true,
  current_stock: '',
  min_stock_level: '',
  max_discount_percentage: '100',
};

const emptyAdjust = { quantity: '', movement_type: 'IN', notes: '' };

const InventoryPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [adjustProduct, setAdjustProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [adjustForm, setAdjustForm] = useState(emptyAdjust);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [adjustSuccess, setAdjustSuccess] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editForm, setEditForm] = useState(emptyProduct);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.INVENTORY.PRODUCTS);
      setProducts(res.data || res || []);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleAddOpen = () => {
    setProductForm(emptyProduct);
    setError(null);
    setSuccess(false);
    setAddOpen(true);
  };

  const handleAddSubmit = async () => {
    if (!productForm.name.trim()) { setError('Product name is required.'); return; }
    if (!productForm.selling_price) { setError('Selling price is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(API_ENDPOINTS.INVENTORY.CREATE_PRODUCT, {
        name: productForm.name.trim(),
        sku: productForm.sku.trim() || undefined,
        category: productForm.category.trim() || undefined,
        brand: productForm.brand.trim() || undefined,
        description: productForm.description.trim() || undefined,
        unit_of_measure: productForm.unit_of_measure || 'unit',
        cost_price: productForm.cost_price ? Number(productForm.cost_price) : undefined,
        selling_price: Number(productForm.selling_price),
        tax_percentage: Number(productForm.tax_percentage) || 0,
        is_taxable: productForm.is_taxable,
        current_stock: Number(productForm.current_stock) || 0,
        min_stock_level: productForm.min_stock_level ? Number(productForm.min_stock_level) : undefined,
        max_discount_percentage: productForm.max_discount_percentage ? Number(productForm.max_discount_percentage) : undefined,
      });
      setSuccess(true);
      await fetchProducts();
      setTimeout(() => setAddOpen(false), 800);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string }; detail?: string } } })?.response?.data?.error?.message
        ?? (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || 'Failed to add product.');
    } finally {
      setSaving(false);
    }
  };

  const openAdjust = (product: Product) => {
    setAdjustProduct(product);
    setAdjustForm(emptyAdjust);
    setAdjustError(null);
    setAdjustSuccess(false);
  };

  const handleAdjustSubmit = async () => {
    if (!adjustProduct || !adjustForm.quantity) { setAdjustError('Quantity is required.'); return; }
    setSaving(true);
    setAdjustError(null);
    try {
      await api.post(API_ENDPOINTS.INVENTORY.ADJUST(adjustProduct.id), {
        quantity: Number(adjustForm.quantity),
        movement_type: adjustForm.movement_type,
        notes: adjustForm.notes,
      });
      setAdjustSuccess(true);
      await fetchProducts();
      setTimeout(() => setAdjustProduct(null), 800);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setAdjustError(msg || 'Failed to adjust stock.');
    } finally {
      setSaving(false);
    }
  };

  const openEditProduct = (product: Product) => {
    setEditProduct(product);
    setEditForm({
      name: product.name,
      sku: product.sku || '',
      category: product.category || '',
      brand: product.brand || '',
      description: product.description || '',
      unit_of_measure: product.unit_of_measure || 'unit',
      cost_price: product.cost_price != null ? String(product.cost_price) : '',
      selling_price: product.selling_price != null ? String(product.selling_price) : '',
      tax_percentage: product.tax_percentage != null ? String(product.tax_percentage) : '0',
      is_taxable: product.is_taxable !== 0,
      current_stock: String(product.current_stock),
      min_stock_level: product.min_stock_level != null ? String(product.min_stock_level) : '',
      max_discount_percentage: product.max_discount_percentage != null ? String(product.max_discount_percentage) : '100',
    });
    setEditError(null);
  };

  const handleEditSubmit = async () => {
    if (!editProduct || !editForm.name.trim()) { setEditError('Product name is required.'); return; }
    if (!editForm.selling_price) { setEditError('Selling price is required.'); return; }
    setEditSaving(true);
    setEditError(null);
    try {
      await api.put(API_ENDPOINTS.INVENTORY.UPDATE_PRODUCT(editProduct.id), {
        name: editForm.name.trim(),
        sku: editForm.sku.trim() || null,
        category: editForm.category.trim() || null,
        brand: editForm.brand.trim() || null,
        description: editForm.description.trim() || null,
        unit_of_measure: editForm.unit_of_measure || 'unit',
        cost_price: editForm.cost_price ? Number(editForm.cost_price) : null,
        selling_price: Number(editForm.selling_price),
        tax_percentage: Number(editForm.tax_percentage) || 0,
        is_taxable: editForm.is_taxable ? 1 : 0,
        min_stock_level: editForm.min_stock_level ? Number(editForm.min_stock_level) : null,
        max_discount_percentage: editForm.max_discount_percentage ? Number(editForm.max_discount_percentage) : null,
      });
      setEditProduct(null);
      await fetchProducts();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setEditError(msg || 'Failed to update product.');
    } finally {
      setEditSaving(false);
    }
  };

  const isLowStock = (product: Product) =>
    product.min_stock_level != null && product.current_stock <= product.min_stock_level;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const ProductFormFields = ({ form, setForm }: { form: typeof emptyProduct; setForm: React.Dispatch<React.SetStateAction<typeof emptyProduct>> }) => (
    <Box display="flex" flexDirection="column" gap={2} mt={1}>
      {/* Basic Info */}
      <Box display="flex" gap={2}>
        <TextField
          label="Product Name *" fullWidth
          value={form.name}
          onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
        />
        <TextField
          label="SKU / Product Code" fullWidth
          value={form.sku}
          onChange={(e) => setForm(p => ({ ...p, sku: e.target.value }))}
        />
      </Box>
      <Box display="flex" gap={2}>
        <TextField
          label="Brand / Manufacturer" fullWidth
          value={form.brand}
          onChange={(e) => setForm(p => ({ ...p, brand: e.target.value }))}
        />
        <TextField
          label="Category" fullWidth
          value={form.category}
          onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
        />
      </Box>
      <TextField
        label="Description" fullWidth multiline rows={2}
        value={form.description}
        onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
      />

      <Divider />

      {/* Pricing */}
      <Typography variant="subtitle2" color="text.secondary">Pricing</Typography>
      <Box display="flex" gap={2}>
        <TextField
          label="Cost Price ($)" type="number" fullWidth
          value={form.cost_price}
          onChange={(e) => setForm(p => ({ ...p, cost_price: e.target.value }))}
          inputProps={{ min: 0, step: 0.01 }}
        />
        <TextField
          label="Selling Price ($) *" type="number" fullWidth
          value={form.selling_price}
          onChange={(e) => setForm(p => ({ ...p, selling_price: e.target.value }))}
          inputProps={{ min: 0, step: 0.01 }}
        />
      </Box>
      <Box display="flex" gap={2} alignItems="center">
        <TextField
          label="Tax %" type="number" fullWidth
          value={form.tax_percentage}
          onChange={(e) => setForm(p => ({ ...p, tax_percentage: e.target.value }))}
          inputProps={{ min: 0, max: 100, step: 0.1 }}
        />
        <TextField
          label="Max Discount %" type="number" fullWidth
          value={form.max_discount_percentage}
          onChange={(e) => setForm(p => ({ ...p, max_discount_percentage: e.target.value }))}
          inputProps={{ min: 0, max: 100 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.is_taxable}
              onChange={(e) => setForm(p => ({ ...p, is_taxable: e.target.checked }))}
            />
          }
          label="Taxable"
          sx={{ whiteSpace: 'nowrap' }}
        />
      </Box>

      <Divider />

      {/* Stock */}
      <Typography variant="subtitle2" color="text.secondary">Stock</Typography>
      <Box display="flex" gap={2}>
        <TextField
          select label="Unit of Measure" fullWidth
          value={form.unit_of_measure}
          onChange={(e) => setForm(p => ({ ...p, unit_of_measure: e.target.value }))}
        >
          {UNIT_OPTIONS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
        </TextField>
        <TextField
          label="Current Stock" type="number" fullWidth
          value={form.current_stock}
          onChange={(e) => setForm(p => ({ ...p, current_stock: e.target.value }))}
          inputProps={{ min: 0 }}
        />
        <TextField
          label="Min Stock Level" type="number" fullWidth
          value={form.min_stock_level}
          onChange={(e) => setForm(p => ({ ...p, min_stock_level: e.target.value }))}
          inputProps={{ min: 0 }}
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Inventory</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOpen}>
          Add Product
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Min Stock</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">No products found</TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.id} hover sx={{ bgcolor: isLowStock(product) ? 'warning.50' : 'inherit' }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                    {product.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {product.description.length > 50 ? product.description.slice(0, 50) + '…' : product.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="body2">{product.sku || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{product.brand || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{product.category || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{product.unit_of_measure || 'unit'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{product.current_stock}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{product.min_stock_level ?? '—'}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {product.cost_price != null ? `$${Number(product.cost_price).toFixed(2)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {product.selling_price != null ? `$${Number(product.selling_price).toFixed(2)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isLowStock(product) ? (
                      <Chip label="Low Stock" color="warning" size="small" />
                    ) : (
                      <Chip label="OK" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditProduct(product)} sx={{ mr: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button size="small" variant="outlined" onClick={() => openAdjust(product)}>
                      Adjust
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Product Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Product</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Product added successfully.</Alert>}
          <ProductFormFields form={productForm} setForm={setProductForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={!!editProduct} onClose={() => setEditProduct(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Product — {editProduct?.name}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <ProductFormFields form={editForm} setForm={setEditForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProduct(null)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={editSaving}>
            {editSaving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={!!adjustProduct} onClose={() => setAdjustProduct(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Stock — {adjustProduct?.name}</DialogTitle>
        <DialogContent>
          {adjustError && <Alert severity="error" sx={{ mb: 2 }}>{adjustError}</Alert>}
          {adjustSuccess && <Alert severity="success" sx={{ mb: 2 }}>Stock adjusted.</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Current stock: <strong>{adjustProduct?.current_stock}</strong> {adjustProduct?.unit_of_measure || 'units'}
            </Typography>
            <TextField
              label="Quantity" type="number" fullWidth
              value={adjustForm.quantity}
              onChange={(e) => setAdjustForm((f) => ({ ...f, quantity: e.target.value }))}
              inputProps={{ min: 1 }}
            />
            <TextField
              select label="Movement Type" fullWidth
              value={adjustForm.movement_type}
              onChange={(e) => setAdjustForm((f) => ({ ...f, movement_type: e.target.value }))}
            >
              <MenuItem value="IN">IN — Receive Stock</MenuItem>
              <MenuItem value="OUT">OUT — Dispense / Use</MenuItem>
              <MenuItem value="ADJUSTMENT">ADJUSTMENT — Manual Correction</MenuItem>
              <MenuItem value="EXPIRED">EXPIRED — Write Off</MenuItem>
              <MenuItem value="DAMAGED">DAMAGED — Write Off</MenuItem>
            </TextField>
            <TextField
              label="Notes" fullWidth
              value={adjustForm.notes}
              onChange={(e) => setAdjustForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustProduct(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleAdjustSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Adjust'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryPage;
